import { toUtcDateString } from "@dailywars/core/entitlements/mapAccess";
import { ALLTIME_MEDAL_PERIOD } from "@dailywars/core/medals";
import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";

import { PageEyeBrow } from "#/components/PageEyeBrow";
import { PageHeader } from "#/components/PageHeader";
import { PageIntro } from "#/components/PageIntro";
import { DOCUMENT_TITLES, documentHead } from "#/lib/documentTitle";
import { medalHiscoresInfiniteQueryOptions } from "#/lib/queries";
import { hiscoresSearchSchema } from "#/lib/routeSearchSchemas";

import { HISCORES_START_MONTH, HiscoresMonthNav, HiscoresScopeToggle } from "./-hiscores.controls";
import { HiscoresListCard } from "./-hiscores.ui";

export const Route = createFileRoute("/hiscores")({
  head: () =>
    documentHead(DOCUMENT_TITLES.hiscores, {
      description: "All-time and monthly medal standings for Daily Wars.",
      canonicalPath: "/hiscores",
    }),
  validateSearch: hiscoresSearchSchema,
  loaderDeps: ({ search }) => ({ month: search.month }),
  loader: async ({ context, deps }) => {
    const period = deps.month ? clampHiscoresMonthKey(deps.month) : ALLTIME_MEDAL_PERIOD;
    await context.queryClient.ensureInfiniteQueryData(medalHiscoresInfiniteQueryOptions(period));
  },
  component: HiscoresPage,
});

function currentUtcMonthKey(now = new Date()): string {
  return toUtcDateString(now).slice(0, 7);
}

function latestHiscoresMonthKey(now = new Date()): string {
  const current = currentUtcMonthKey(now);
  return current < HISCORES_START_MONTH ? HISCORES_START_MONTH : current;
}

function clampHiscoresMonthKey(monthKey: string): string {
  if (monthKey < HISCORES_START_MONTH) {
    return HISCORES_START_MONTH;
  }
  const latest = latestHiscoresMonthKey();
  if (monthKey > latest) {
    return latest;
  }
  return monthKey;
}

function HiscoresStandings({ month }: { month: string | undefined }) {
  const navigate = useNavigate({ from: "/hiscores" });
  const latestMonthKey = latestHiscoresMonthKey();
  const scope = month ? "monthly" : "alltime";
  const monthKey = month
    ? clampHiscoresMonthKey(month)
    : clampHiscoresMonthKey(currentUtcMonthKey());
  const period = scope === "alltime" ? ALLTIME_MEDAL_PERIOD : monthKey;

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } = useInfiniteQuery(
    medalHiscoresInfiniteQueryOptions(period),
  );

  const flatRows = useMemo(() => data?.pages.flatMap((page) => page.entries) ?? [], [data]);

  return (
    <>
      <HiscoresScopeToggle
        scope={scope}
        onScopeChange={(next) => {
          void navigate({
            search: next === "monthly" ? { month: monthKey } : {},
          });
        }}
      />

      {scope === "monthly" ? (
        <HiscoresMonthNav
          monthKey={monthKey}
          onMonthChange={(next) => {
            void navigate({
              search: { month: clampHiscoresMonthKey(next) },
            });
          }}
          latestMonthKey={latestMonthKey}
        />
      ) : null}

      <HiscoresListCard
        isPending={isPending}
        rows={flatRows}
        hasNextPage={hasNextPage ?? false}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
      />
    </>
  );
}

function HiscoresPage() {
  const { month } = Route.useSearch();

  return (
    <main className="mx-auto w-full max-w-[1080px] px-4 py-10">
      <div className="mx-auto flex w-full max-w-lg flex-col items-center gap-6">
        <PageIntro>
          <PageEyeBrow>Rankings</PageEyeBrow>
          <PageHeader>Hiscores</PageHeader>
        </PageIntro>
        <HiscoresStandings month={month} />
      </div>
    </main>
  );
}
